/**
 * Global subscription product form — gift line item properties and annual free gift cart sync.
 */
(() => {
  const GSP_PARENT_KEY = '_gsp_key';
  const GSP_LINK_KEY = '_gsp_parent_key';
  const DEFAULT_PROMO_PROPERTY = '_promo_product';
  const LEGACY_PROMO_PROPERTY = 'promo_product';
  const INTERNAL_PROPERTY_KEYS = new Set([
    GSP_PARENT_KEY,
    GSP_LINK_KEY,
    DEFAULT_PROMO_PROPERTY,
    LEGACY_PROMO_PROPERTY
  ]);

  const collectLineItemProperties = (form) => {
    const formData = new FormData(form);
    const properties = {};
    const propertyPattern = /^properties\[(.*?)\]$/;

    formData.forEach((value, key) => {
      const match = propertyPattern.exec(key);
      if (!match) return;

      const propertyValue = value?.toString().trim();
      if (propertyValue) properties[match[1]] = propertyValue;
    });

    return properties;
  };

  const parseAnnualVariantIds = (value) => (
    (value || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean)
  );

  const getSelectedFreeGiftVariantId = (formElement) => {
    const selected = formElement.querySelector('[data-gsp-free-gift-variant]:checked');
    if (selected?.value) return Number(selected.value);

    const fallback = formElement.querySelector('[data-gsp-free-gift-variant]');
    return fallback?.value ? Number(fallback.value) : 0;
  };

  const isAnnualVariant = (formElement, variantId) => (
    parseAnnualVariantIds(formElement.dataset.gspAnnualVariantIds).includes(String(variantId))
  );

  const shouldAttachFreeGift = (formElement, variantId) => {
    const freeGiftVariantId = getSelectedFreeGiftVariantId(formElement);
    return (
      formElement.dataset.gspFreeGiftEnabled === 'true'
      && isAnnualVariant(formElement, variantId)
      && Number.isFinite(freeGiftVariantId)
      && freeGiftVariantId > 0
    );
  };

  const validateFreeGiftSelection = (formElement, variantId) => {
    if (formElement.dataset.gspFreeGiftEnabled !== 'true' || !isAnnualVariant(formElement, variantId)) return true;

    const freeGiftVariantId = getSelectedFreeGiftVariantId(formElement);
    if (Number.isFinite(freeGiftVariantId) && freeGiftVariantId > 0) return true;

    const selector = formElement.querySelector('[data-gsp-free-gift-selector]');
    selector?.removeAttribute('hidden');
    selector?.querySelector('[data-gsp-free-gift-variant]')?.focus();
    Utility.announceToScreenReader('Please select a free gift.', 'assertive');
    return false;
  };

  const validateGiftForm = (formElement) => {
    const giftToggle = formElement.querySelector('[data-gsp-gift-toggle]');
    if (!giftToggle?.checked) return true;

    const emailField = formElement.querySelector('[data-gsp-gift-email]');
    const emailValue = emailField?.value?.trim() || '';

    if (!emailValue || !Utility.Form.validateEmail(emailValue)) {
      emailField?.setAttribute('aria-invalid', 'true');
      emailField?.focus();
      Utility.announceToScreenReader('Please enter a valid gift recipient email.', 'assertive');
      return false;
    }

    emailField?.removeAttribute('aria-invalid');
    return true;
  };

  const getSelectedSellingPlanId = (formElement) => {
    const sellingPlanInput = formElement.querySelector('[data-gsp-selling-plan-id]');
    const sellingPlanId = Number(sellingPlanInput?.value);
    return Number.isFinite(sellingPlanId) && sellingPlanId > 0 ? sellingPlanId : null;
  };

  const buildGspCartItems = (formElement) => {
    const variantInput = formElement.querySelector('[data-gsp-variant-id]');
    const variantId = Number(variantInput?.value);
    const quantityInput = formElement.querySelector('[name="quantity"]');
    const quantity = Number(quantityInput?.value || 1);
    const properties = collectLineItemProperties(formElement.form);
    const linkKey = `gsp-${Date.now()}`;
    const attachFreeGift = shouldAttachFreeGift(formElement, variantInput?.value);
    const sellingPlanId = getSelectedSellingPlanId(formElement);

    if (!Number.isFinite(variantId) || variantId <= 0) {
      throw new Error('Please select a subscription plan.');
    }

    const subscriptionItem = {
      id: variantId,
      quantity: Number.isNaN(quantity) || quantity < 1 ? 1 : quantity,
      properties: attachFreeGift
        ? { ...properties, [GSP_PARENT_KEY]: linkKey }
        : properties
    };

    if (sellingPlanId) {
      subscriptionItem.selling_plan = sellingPlanId;
    }

    // Free gift first, then annual subscription — Shopify cart/add processes items in array order.
    const items = [];

    if (attachFreeGift) {
      const promoProperty = formElement.dataset.gspPromoProperty || DEFAULT_PROMO_PROPERTY;
      items.push(createLinkedGiftItem(getSelectedFreeGiftVariantId(formElement), linkKey, promoProperty));
    }

    items.push(subscriptionItem);

    return items;
  };

  const normalizeCartAddResponse = (response) => {
    if (!response?.items?.length || response.title) return response;

    // Prefer the subscription parent line over the promo gift when both were added.
    const primaryItem = response.items.find((item) => item.properties?.[GSP_PARENT_KEY])
      || response.items.find((item) => !(
        item.properties?.[DEFAULT_PROMO_PROPERTY]
        || item.properties?.[LEGACY_PROMO_PROPERTY]
      ))
      || response.items[0];

    return {
      ...primaryItem,
      sections: response.sections
    };
  };

  const isSeasonalCartLine = (item, keyword) => {
    if (!item) return false;
    const normalizedKeyword = (keyword || 'season').toLowerCase();
    const planName = item.selling_plan_allocation?.selling_plan?.name?.toLowerCase() || '';
    const variantTitle = (item.variant_title || '').toLowerCase();
    const optionValues = (item.options_with_values || [])
      .map((option) => String(option.value || '').toLowerCase())
      .join(' ');

    return [planName, variantTitle, optionValues].some((value) => value.includes(normalizedKeyword));
  };

  const collectCartLineProperties = (item) => {
    const properties = {};
    if (!item?.properties) return properties;

    Object.entries(item.properties).forEach(([key, value]) => {
      if (value == null || !String(value).trim() || INTERNAL_PROPERTY_KEYS.has(key)) return;
      properties[key] = String(value);
    });

    return properties;
  };

  const resolveFreeGiftVariantId = (config, cart) => {
    const configuredId = Number(config.freeGiftVariantId);
    if (Number.isFinite(configuredId) && configuredId > 0) return configuredId;

    const pageGiftInput = document.querySelector('[data-gsp-free-gift-variant]:checked, [data-gsp-free-gift-variant]');
    const pageGiftId = Number(pageGiftInput?.value);
    if (Number.isFinite(pageGiftId) && pageGiftId > 0) return pageGiftId;

    const promoProperty = config.promoProperty || DEFAULT_PROMO_PROPERTY;
    const promoItem = (cart?.items || []).find((item) => (
      item.properties?.[promoProperty]
      || item.properties?.[LEGACY_PROMO_PROPERTY]
      || item.properties?.[GSP_LINK_KEY]
    ));

    const cartGiftId = Number(promoItem?.variant_id);
    if (Number.isFinite(cartGiftId) && cartGiftId > 0) return cartGiftId;

    return null;
  };

  const createLinkedGiftItem = (freeGiftVariantId, linkKey, promoProperty = DEFAULT_PROMO_PROPERTY) => ({
    id: freeGiftVariantId,
    quantity: 1,
    properties: {
      [promoProperty]: 'true',
      [GSP_LINK_KEY]: linkKey
    }
  });

  const buildAnnualUpgradeItems = (config, properties = {}, cart = null) => {
    const linkKey = `gsp-${Date.now()}`;
    const promoProperty = config.promoProperty || DEFAULT_PROMO_PROPERTY;
    const subscriptionItem = {
      id: Number(config.annualVariantId),
      quantity: 1,
      selling_plan: Number(config.annualSellingPlanId),
      properties: {
        ...properties,
        [GSP_PARENT_KEY]: linkKey
      }
    };

    const freeGiftVariantId = resolveFreeGiftVariantId(config, cart);
    const giftItem = Number.isFinite(freeGiftVariantId) && freeGiftVariantId > 0
      ? createLinkedGiftItem(freeGiftVariantId, linkKey, promoProperty)
      : null;

    return { subscriptionItem, giftItem, linkKey };
  };

  const resolveUpgradeConfig = (triggerButton, baseConfig = {}) => {
    const annualVariantId = Number(triggerButton.dataset.annualVariantId) || Number(baseConfig.annualVariantId);
    const annualSellingPlanId = Number(triggerButton.dataset.annualSellingPlanId) || Number(baseConfig.annualSellingPlanId);
    const freeGiftVariantId = Number(triggerButton.dataset.freeGiftVariantId) || Number(baseConfig.freeGiftVariantId);

    return {
      ...baseConfig,
      annualVariantId: Number.isFinite(annualVariantId) && annualVariantId > 0 ? annualVariantId : null,
      annualSellingPlanId: Number.isFinite(annualSellingPlanId) && annualSellingPlanId > 0 ? annualSellingPlanId : null,
      freeGiftVariantId: Number.isFinite(freeGiftVariantId) && freeGiftVariantId > 0 ? freeGiftVariantId : null,
      seasonalKeyword: baseConfig.seasonalKeyword || 'season',
      promoProperty: baseConfig.promoProperty || DEFAULT_PROMO_PROPERTY
    };
  };

  const upgradeSeasonalToAnnual = async (cartForm, triggerButton) => {
    if (cartForm._isRequestInFlight) {
      Utility.Animate.setLoading(triggerButton, false);
      return;
    }

    const config = resolveUpgradeConfig(triggerButton, globalThis.themeData?.cartUpgrade || {});
    if (!config.annualVariantId || !config.annualSellingPlanId) {
      Utility.Animate.setLoading(triggerButton, false);
      Utility.handleError('Unable to upgrade subscription. Annual plan is not configured.', 'Cart Upgrade');
      Utility.announceToScreenReader('Unable to upgrade subscription.', 'assertive');
      return;
    }

    const sectionId = cartForm.dataset.sectionId;
    const line = Number.parseInt(triggerButton.dataset.upgradeLine, 10);
    if (!sectionId || Number.isNaN(line) || line < 1) {
      Utility.Animate.setLoading(triggerButton, false);
      return;
    }

    cartForm._isRequestInFlight = true;
    cartForm._setCartFormDisabled?.(true);
    Utility.Animate.setLoading(triggerButton, true, { disable: true });

    try {
      const cart = await Utility.Fetch.json(`${globalThis.routes?.cart_url || '/cart'}.js`);
      const seasonalItem = cart.items?.[line - 1];

      if (!isSeasonalCartLine(seasonalItem, config.seasonalKeyword)) {
        throw new Error('Seasonal subscription not found in cart.');
      }

      const properties = collectCartLineProperties(seasonalItem);

      await Utility.Fetch.json(`${globalThis.routes?.cart_change_url || '/cart/change'}.js`, {
        ...Utility.Fetch.config(),
        body: JSON.stringify({ line, quantity: 0 })
      });

      const { subscriptionItem, giftItem } = buildAnnualUpgradeItems(config, properties, cart);
      const addConfig = Utility.Fetch.config('json');

      // Free gift first, then annual subscription.
      if (giftItem) {
        await Utility.Fetch.json(`${globalThis.routes?.cart_add_url || '/cart/add'}.js`, {
          ...addConfig,
          body: JSON.stringify({
            items: [giftItem],
            sections: sectionId
          })
        });
      }

      const addResponse = await Utility.Fetch.json(`${globalThis.routes?.cart_add_url || '/cart/add'}.js`, {
        ...addConfig,
        body: JSON.stringify({
          items: [subscriptionItem],
          sections: sectionId
        })
      });

      const cartSection = addResponse.sections?.[sectionId];
      if (!cartSection) {
        throw new Error('Failed to refresh cart after upgrade.');
      }

      eventBus.publish(THEME_EVENTS.cartUpdate, {
        data: {
          action: 'upgrade-to-annual',
          section: Utility.DOM.parse(cartSection),
          elementToFocus: triggerButton
        }
      });

      Utility.announceToScreenReader('Upgraded to annual subscription.', 'assertive');
    } catch (error) {
      const message = Utility.extractErrorMessage(error) || 'Failed to upgrade subscription';
      Utility.handleError(message, 'Cart Upgrade');
      Utility.announceToScreenReader(message, 'assertive');
    } finally {
      cartForm._isRequestInFlight = false;
      cartForm._setCartFormDisabled?.(false);
      Utility.Animate.setLoading(triggerButton, false);
    }
  };

  const initCartUpgradeHandler = () => {
    if (document.documentElement.dataset.gspCartUpgradeBound) return;
    document.documentElement.dataset.gspCartUpgradeBound = 'true';

    document.addEventListener('click', (event) => {
      const triggerButton = event.target instanceof Element
        ? event.target.closest('[data-upgrade-to-annual]')
        : null;
      if (!triggerButton) return;

      const cartForm = triggerButton.closest('cart-form');
      if (!cartForm) return;

      event.preventDefault();
      Utility.Animate.setLoading(triggerButton, true, { disable: false });
      upgradeSeasonalToAnnual(cartForm, triggerButton);
    });
  };

  const isPromoGiftItem = (item) => (
    item?.properties?.[DEFAULT_PROMO_PROPERTY]
    || item?.properties?.[LEGACY_PROMO_PROPERTY]
    || item?.properties?.[GSP_LINK_KEY]
  );

  const isAnnualSubscriptionItem = (item) => {
    if (!item) return false;

    const variantTitle = (item.variant_title || '').toLowerCase();
    const planName = item.selling_plan_allocation?.selling_plan?.name?.toLowerCase() || '';
    const optionValues = (item.options_with_values || [])
      .map((option) => String(option.value || '').toLowerCase())
      .join(' ');

    return [variantTitle, planName, optionValues].some((value) => value.includes('annual'));
  };

  const removeCartLines = async (lines, sectionId) => {
    if (!lines.length) return null;

    let lastResponse = null;

    for (const line of [...lines].sort((a, b) => b - a)) {
      lastResponse = await Utility.Fetch.json(globalThis.routes?.cart_change_url || '/cart/change.js', {
        ...Utility.Fetch.config(),
        body: JSON.stringify({
          line,
          quantity: 0,
          sections: sectionId
        })
      });
    }

    return lastResponse;
  };

  const removeLinkedGifts = async (parentKey, sectionId) => {
    if (!parentKey) return null;

    const cart = await Utility.Fetch.json(`${globalThis.routes?.cart_url || '/cart'}.js`);
    const linkedLines = (cart.items || [])
      .map((item, index) => ({ item, line: index + 1 }))
      .filter(({ item }) => item.properties?.[GSP_LINK_KEY] === parentKey)
      .map(({ line }) => line);

    return removeCartLines(linkedLines, sectionId);
  };

  const removePromoGifts = async (sectionId) => {
    const cart = await Utility.Fetch.json(`${globalThis.routes?.cart_url || '/cart'}.js`);
    const promoLines = (cart.items || [])
      .map((item, index) => ({ item, line: index + 1 }))
      .filter(({ item }) => isPromoGiftItem(item))
      .map(({ line }) => line);

    return removeCartLines(promoLines, sectionId);
  };

  const clearCartIfZeroValueRemaining = async (sectionId) => {
    const cart = await Utility.Fetch.json(`${globalThis.routes?.cart_url || '/cart'}.js`);

    if ((cart.item_count || 0) <= 0 || (cart.total_price || 0) !== 0) {
      return null;
    }

    return Utility.Fetch.json(`${globalThis.routes?.cart_url || '/cart'}/clear.js`, {
      ...Utility.Fetch.config(),
      body: JSON.stringify({ sections: sectionId })
    });
  };

  const resolveRemovalContext = async (cartFormElement, line) => {
    const lineIndex = Number.parseInt(line, 10) - 1;
    if (lineIndex < 0) return { parentKey: null, item: null };

    const cartItemEl = cartFormElement.cartForm?.querySelector(`[data-cart-item][data-line="${line}"]`);
    const domKey = cartItemEl?.dataset?.gspKey;
    if (domKey) {
      return { parentKey: domKey, item: null };
    }

    const themeItem = globalThis.themeData?.cart?.items?.[lineIndex];
    const themeKey = themeItem?.properties?.[GSP_PARENT_KEY];
    if (themeKey) {
      return { parentKey: themeKey, item: themeItem };
    }

    const cart = await Utility.Fetch.json(`${globalThis.routes?.cart_url || '/cart'}.js`);
    const item = cart.items?.[lineIndex];

    return {
      parentKey: item?.properties?.[GSP_PARENT_KEY] || null,
      item: item || null
    };
  };

  const patchCartForm = () => {
    const CartForm = customElements.get('cart-form');
    if (!CartForm || CartForm.prototype._gspCartPatched) return;

    const originalUpdateItem = CartForm.prototype.updateItem;

    CartForm.prototype.updateItem = async function updateItemWithGspSync(line, quantity, properties = null) {
      const isRemove = Number.parseInt(quantity, 10) === 0;
      let removalContext = { parentKey: null, item: null };

      if (isRemove) {
        removalContext = await resolveRemovalContext(this, line);
      }

      await originalUpdateItem.call(this, line, quantity, properties);

      if (!isRemove || !this.isConnected) return;

      try {
        const sectionId = this.dataset.sectionId;
        let cartRefreshResponse = null;

        if (removalContext.parentKey) {
          cartRefreshResponse = await removeLinkedGifts(removalContext.parentKey, sectionId);
        } else if (isAnnualSubscriptionItem(removalContext.item)) {
          cartRefreshResponse = await removePromoGifts(sectionId);
        }

        const clearedResponse = await clearCartIfZeroValueRemaining(sectionId);
        if (clearedResponse?.sections?.[sectionId]) {
          cartRefreshResponse = clearedResponse;
        }

        const cartSection = cartRefreshResponse?.sections?.[sectionId];

        if (cartSection) {
          eventBus.publish(THEME_EVENTS.cartUpdate, {
            data: {
              action: clearedResponse ? 'clear-zero-value-cart' : 'remove-linked-gift',
              section: cartSection
            }
          });
        }
      } catch (error) {
        Utility.handleError(Utility.extractErrorMessage(error) || 'Failed to remove linked gift', 'Cart Update');
      }
    };

    CartForm.prototype._gspCartPatched = true;
  };

  const patchProductForm = () => {
    if (ProductForm.prototype._gspPatched) return;

    const originalOnSubmitHandler = ProductForm.prototype.onSubmitHandler;

    ProductForm.prototype.onSubmitHandler = async function onSubmitHandlerWithGsp(evt) {
      if (!this.hasAttribute('data-gsp-subscription')) {
        return originalOnSubmitHandler.call(this, evt);
      }

      evt.preventDefault();

      const submitButton = evt.submitter || this.submitButton;
      if (this._isSubmitting) return;

      Utility.Animate.setLoading(submitButton, true, { disable: false });

      if (!validateGiftForm(this)) {
        Utility.Animate.setLoading(submitButton, false);
        return;
      }

      if (!validateFreeGiftSelection(this, this.querySelector('[data-gsp-variant-id]')?.value)) {
        Utility.Animate.setLoading(submitButton, false);
        return;
      }

      this._isSubmitting = true;
      Utility.Animate.setLoading(submitButton, true, { disable: true });

      try {
        const payload = {
          items: buildGspCartItems(this),
          sections: this.cartSectionId
        };
        const res = await Utility.Fetch.json(
          `${globalThis.routes?.cart_add_url || '/cart/add'}.js`,
          Utility.Fetch.config('json', { body: JSON.stringify(payload) })
        );

        if (this.shouldRedirectToCheckout()) {
          window.location.assign('/checkout');
          return;
        }

        this.handleSubmitResponse(normalizeCartAddResponse(res));
        this.resetGspForm?.();
      } catch (error) {
        const message = Utility.extractErrorMessage(error) || 'Failed to add to cart';
        Utility.handleError(message, 'Add to Cart');
        Utility.announceToScreenReader(message, 'assertive');
      } finally {
        this._isSubmitting = false;
        Utility.Animate.setLoading(submitButton, false);
      }
    };

    ProductForm.prototype._gspPatched = true;

    document.querySelectorAll('product-form[data-gsp-subscription]').forEach((formElement) => {
      if (!formElement.form || !formElement.boundOnSubmitHandler) return;

      formElement.form.removeEventListener('submit', formElement.boundOnSubmitHandler);
      formElement.boundOnSubmitHandler = formElement.onSubmitHandler.bind(formElement);
      formElement.form.addEventListener('submit', formElement.boundOnSubmitHandler);
    });
  };

  customElements.whenDefined('product-form').then(() => {
    patchProductForm();
    customElements.whenDefined('cart-form').then(patchCartForm);
  });

  initCartUpgradeHandler();
})();
